import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../store/authStore'
import { userAPI, notificationAPI, NotificationItem } from '../../api/client'

interface NavItem {
  icon: string
  label: string
  path: string
  iconFill?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { icon: 'bolt', label: 'Dashboard', path: '/dashboard', iconFill: true },
  { icon: 'calendar_today', label: 'Session', path: '/session' },
  { icon: 'library_books', label: 'Thư viện', path: '/library' },
  { icon: 'headphones', label: 'Nghe', path: '/listening' },
  { icon: 'menu_book', label: 'Đọc', path: '/reading' },
  { icon: 'edit_note', label: 'Viết', path: '/writing' },
  { icon: 'mic', label: 'Nói', path: '/speaking' },
  { icon: 'style', label: 'Từ vựng', path: '/vocab' },
  { icon: 'leaderboard', label: 'Tiến độ', path: '/progress' },
  { icon: 'emoji_events', label: 'Thách thức', path: '/challenges' },
  { icon: 'quiz', label: 'Thi thử', path: '/mock-test' },
]

const BOTTOM_NAV: NavItem[] = [
  { icon: 'bolt', label: 'Home', path: '/dashboard', iconFill: true },
  { icon: 'calendar_today', label: 'Session', path: '/session' },
  { icon: 'style', label: 'Vocab', path: '/vocab' },
  { icon: 'leaderboard', label: 'Progress', path: '/progress' },
  { icon: 'emoji_events', label: 'Challenges', path: '/challenges' },
]

interface AppLayoutProps {
  children: React.ReactNode
}

// Vietnamese relative time for notification timestamps.
function timeAgo(iso: string): string {
  const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z').getTime()
  const diff = Math.max(0, Date.now() - then)
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'Vừa xong'
  if (min < 60) return `${min} phút trước`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} giờ trước`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} ngày trước`
  return new Date(then).toLocaleDateString('vi-VN')
}

export default function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { clearAuth, token } = useAuthStore()

  const qc = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const notifRef = useRef<HTMLDivElement>(null)

  const { data: userMe } = useQuery({
    queryKey: ['user-me'],
    queryFn: userAPI.me,
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })

  const displayName: string = (userMe as any)?.name || (userMe as any)?.email || 'Học viên'
  const email: string = (userMe as any)?.email ?? ''
  const initial = displayName.trim().charAt(0).toUpperCase() || 'U'

  // Unread badge — polls every 60s and on window focus; cheap count endpoint.
  const { data: unread } = useQuery({
    queryKey: ['notif-unread'],
    queryFn: notificationAPI.unreadCount,
    enabled: !!token,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  })
  const unreadCount = unread?.count ?? 0

  // Full list — only fetched while the panel is open.
  const { data: notifications, isLoading: notifLoading } = useQuery({
    queryKey: ['notif-list'],
    queryFn: () => notificationAPI.list(1),
    enabled: !!token && notifOpen,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => notificationAPI.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-unread'] })
      qc.invalidateQueries({ queryKey: ['notif-list'] })
    },
  })
  const markAllRead = useMutation({
    mutationFn: () => notificationAPI.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-unread'] })
      qc.invalidateQueries({ queryKey: ['notif-list'] })
    },
  })

  const openNotification = (n: NotificationItem) => {
    if (!n.isRead) markRead.mutate(n.id)
    setNotifOpen(false)
    if (n.link) navigate(n.link)
  }

  const handleLogout = () => {
    clearAuth()
    navigate('/login')
  }

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQuery.trim()
    navigate(q ? `/library?q=${encodeURIComponent(q)}` : '/library')
  }

  // Close the user menu / notification panel on outside click or Escape
  useEffect(() => {
    if (!menuOpen && !notifOpen) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMenuOpen(false); setNotifOpen(false) }
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, notifOpen])

  const isActive = (path: string) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path))

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', fontFamily: 'var(--font-body)' }}>
      {/* ── Top Nav ── */}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1.5rem',
          height: '4rem',
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          boxShadow: '0 20px 40px rgba(0,75,226,0.06)',
        }}
      >
        {/* Brand */}
        <Link
          to="/dashboard"
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '2.25rem', height: '2.25rem', borderRadius: '0.75rem', flexShrink: 0,
              background: 'linear-gradient(135deg, #004be2 0%, #809bff 100%)',
              boxShadow: '0 4px 12px rgba(0,75,226,0.25)',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1", color: 'white' }}>bolt</span>
          </div>
          <span style={{ fontFamily: 'var(--font-headline)', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: 'var(--primary)' }}>
            EngHustle
          </span>
        </Link>

        {/* Desktop center nav */}
        <div className="nav-center-links">
          {[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Thư viện', path: '/library' },
            { label: 'Session', path: '/session' },
            { label: 'Tiến độ', path: '/progress' },
          ].map(item => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                fontFamily: 'var(--font-headline)',
                fontWeight: isActive(item.path) ? 700 : 500,
                fontSize: '0.875rem',
                color: isActive(item.path) ? 'var(--primary)' : 'var(--on-surface-variant)',
                textDecoration: 'none',
                padding: '0.375rem 0.875rem',
                borderRadius: '9999px',
                borderBottom: isActive(item.path) ? '2px solid var(--primary)' : '2px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Global search — Enter jumps to the Library filtered by query */}
          <form
            onSubmit={submitSearch}
            className="nav-search-lg"
            style={{ background: 'var(--surface-container)', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '9999px' }}
          >
            <button
              type="submit"
              aria-label="Tìm kiếm"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--on-surface-variant)', display: 'flex', padding: 0 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>search</span>
            </button>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm kiếm bài học..."
              style={{ background: 'none', border: 'none', outline: 'none', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--on-surface)', width: '9rem' }}
            />
          </form>

          {/* Search shortcut — mobile / tablet (no room for the input) */}
          <button
            onClick={() => navigate('/library')}
            className="nav-mobile-only"
            aria-label="Tìm kiếm"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--on-surface-variant)', padding: '0.375rem', borderRadius: '9999px' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>search</span>
          </button>

          {/* Notifications — bell + unread badge + dropdown inbox */}
          <div ref={notifRef} style={{ position: 'relative', display: 'flex' }}>
            <button
              onClick={() => setNotifOpen((o) => !o)}
              aria-label="Thông báo"
              aria-haspopup="menu"
              aria-expanded={notifOpen}
              style={{ position: 'relative', background: notifOpen ? 'var(--surface-container)' : 'none', border: 'none', cursor: 'pointer', color: notifOpen ? 'var(--primary)' : 'var(--on-surface-variant)', padding: '0.375rem', borderRadius: '9999px', display: 'flex' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.375rem', fontVariationSettings: unreadCount > 0 ? "'FILL' 1" : "'FILL' 0" }}>notifications</span>
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute', top: '0.125rem', right: '0.125rem',
                    minWidth: '1.05rem', height: '1.05rem', padding: '0 0.25rem',
                    borderRadius: '9999px', background: 'var(--error)', color: 'white',
                    fontSize: '0.625rem', fontWeight: 800, lineHeight: '1.05rem', textAlign: 'center',
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.85)',
                  }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
                  width: '22rem', maxWidth: 'calc(100vw - 2rem)',
                  background: 'white', borderRadius: '1rem',
                  boxShadow: '0 20px 40px rgba(39,46,66,0.14)', border: '1px solid rgba(165,173,198,0.15)',
                  zIndex: 60, overflow: 'hidden',
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', borderBottom: '1px solid rgba(165,173,198,0.15)' }}>
                  <span style={{ fontFamily: 'var(--font-headline)', fontWeight: 800, fontSize: '0.9375rem', color: 'var(--on-surface)' }}>Thông báo</span>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllRead.mutate()}
                      disabled={markAllRead.isPending}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 700, padding: 0 }}
                    >
                      Đánh dấu tất cả đã đọc
                    </button>
                  )}
                </div>

                {/* List */}
                <div className="no-scrollbar" style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                  {notifLoading ? (
                    <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--on-surface-variant)', fontSize: '0.8125rem' }}>Đang tải…</div>
                  ) : (notifications ?? []).length === 0 ? (
                    <div style={{ padding: '2.5rem 1rem', textAlign: 'center' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '2.5rem', color: 'var(--outline)', display: 'block', marginBottom: '0.5rem' }}>notifications_off</span>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--on-surface-variant)' }}>Chưa có thông báo nào</div>
                    </div>
                  ) : (
                    (notifications ?? []).map((n) => (
                      <button
                        key={n.id}
                        role="menuitem"
                        onClick={() => openNotification(n)}
                        style={{
                          display: 'flex', gap: '0.75rem', width: '100%', textAlign: 'left',
                          alignItems: 'flex-start', padding: '0.75rem 1rem', border: 'none', cursor: 'pointer',
                          background: n.isRead ? 'none' : 'var(--surface-container-low)',
                          borderBottom: '1px solid rgba(165,173,198,0.1)',
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            flexShrink: 0, fontSize: '1.125rem', color: 'var(--primary)',
                            width: '2.25rem', height: '2.25rem', borderRadius: '0.625rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--primary-container)',
                          }}
                        >
                          {n.icon || 'notifications'}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: n.isRead ? 600 : 800, fontSize: '0.8125rem', color: 'var(--on-surface)', marginBottom: '0.125rem' }}>{n.title}</span>
                          {n.body && <span style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.body}</span>}
                          <span style={{ display: 'block', fontSize: '0.6875rem', color: 'var(--outline)', marginTop: '0.25rem' }}>{timeAgo(n.createdAt)}</span>
                        </span>
                        {!n.isRead && <span style={{ flexShrink: 0, width: '0.5rem', height: '0.5rem', borderRadius: '9999px', background: 'var(--primary)', marginTop: '0.375rem' }} />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/settings')}
            aria-label="Cài đặt"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: isActive('/settings') ? 'var(--primary)' : 'var(--on-surface-variant)', padding: '0.375rem', borderRadius: '9999px' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>settings</span>
          </button>

          {/* Logout button — mobile only */}
          <button
            onClick={handleLogout}
            className="nav-mobile-only"
            aria-label="Đăng xuất"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)', padding: '0.375rem', borderRadius: '9999px' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>logout</span>
          </button>

          {/* User avatar + dropdown menu — desktop */}
          <div className="nav-avatar-desktop" ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Tài khoản"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{
                width: '2.25rem', height: '2.25rem', borderRadius: '9999px',
                overflow: 'hidden', flexShrink: 0, padding: 0,
                border: '2px solid var(--primary-container)', cursor: 'pointer',
                background: 'var(--primary-container)', color: 'var(--on-primary-container)',
                fontWeight: 800, fontSize: '0.875rem', fontFamily: 'var(--font-headline)',
              }}
            >
              {initial}
            </button>

            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
                  minWidth: '13rem', background: 'white', borderRadius: '0.875rem',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(165,173,198,0.15)',
                  padding: '0.5rem', zIndex: 60,
                }}
              >
                <div style={{ padding: '0.5rem 0.75rem 0.625rem' }}>
                  <div style={{ fontFamily: 'var(--font-headline)', fontWeight: 700, fontSize: '0.875rem', color: 'var(--on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                  {email && <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>}
                </div>
                <div style={{ height: '1px', background: 'rgba(165,173,198,0.18)', margin: '0 0.25rem 0.375rem' }} />
                {[
                  { icon: 'account_circle', label: 'Hồ sơ', action: () => navigate('/settings') },
                  { icon: 'workspace_premium', label: 'Nâng cấp Pro', action: () => navigate('/pricing') },
                  { icon: 'help', label: 'Trợ giúp', action: () => navigate('/help') },
                ].map(item => (
                  <button
                    key={item.label}
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); item.action() }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--on-surface)', textAlign: 'left' }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', color: 'var(--on-surface-variant)' }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))}
                <button
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); handleLogout() }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--error)', textAlign: 'left' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>logout</span>
                  Đăng xuất
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Body ── */}
      <div style={{ display: 'flex', paddingTop: '4rem', minHeight: 'calc(100vh - 4rem)' }}>
        {/* ── Sidebar tablet (icon only) ── */}
        <aside className="side-nav-tablet-icon">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="side-nav-item"
              title={item.label}
              style={{
                ...(isActive(item.path) ? {
                  background: 'linear-gradient(135deg, #004be2 0%, #809bff 100%)',
                  color: 'white',
                } : {}),
                textDecoration: 'none',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: isActive(item.path) ? "'FILL' 1" : "'FILL' 0" }}>
                {item.icon}
              </span>
            </Link>
          ))}
        </aside>

        {/* ── Sidebar (desktop) ── */}
        <aside
          className="side-nav-desktop no-scrollbar"
          style={{
            position: 'sticky',
            top: '4rem',
            height: 'calc(100vh - 4rem)',
            width: '15rem',
            background: '#f8f9fe',
            padding: '0.75rem 0.75rem',
            flexShrink: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid rgba(165,173,198,0.12)',
          }}
        >
          {/* AI Coach badge */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '0.625rem',
              marginBottom: '0.5rem', padding: '0.5rem',
              borderRadius: '0.75rem',
              background: 'var(--surface-container-low)',
            }}
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', flexShrink: 0,
                background: 'linear-gradient(135deg, #004be2 0%, #809bff 100%)',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1", color: 'white' }}>psychology</span>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-headline)', fontWeight: 700, fontSize: '0.875rem', color: 'var(--on-surface)' }}>Coach Spark</div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--on-surface-variant)' }}>Sẵn sàng luyện tập!</div>
            </div>
          </div>

          {/* Nav items */}
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.0625rem' }}>
            {NAV_ITEMS.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className="side-nav-item"
                style={{
                  ...(isActive(item.path) ? {
                    background: 'linear-gradient(135deg, #004be2 0%, #809bff 100%)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0,75,226,0.25)',
                  } : {}),
                  textDecoration: 'none',
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: '1.25rem',
                    fontVariationSettings: isActive(item.path) ? "'FILL' 1" : "'FILL' 0",
                  }}
                >
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Bottom actions */}
          <div style={{ marginTop: 'auto', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                width: '100%', padding: '0.75rem', borderRadius: '0.75rem', marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, #004be2 0%, #809bff 100%)',
                color: 'white',
                fontFamily: 'var(--font-headline)',
                fontWeight: 700,
                fontSize: '0.8125rem',
                border: 'none',
                cursor: 'pointer',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Nâng cấp Pro
            </button>
            <button
              onClick={() => navigate('/help')}
              className="side-nav-item"
              style={{ textDecoration: 'none' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>help</span>
              <span>Trợ giúp</span>
            </button>
            <button onClick={handleLogout} className="side-nav-item" style={{ color: 'var(--error)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>logout</span>
              <span>Đăng xuất</span>
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main
          className="main-content"
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: 'hidden',
            padding: '2rem',
            paddingBottom: '5rem',
            minHeight: 'calc(100vh - 4rem)',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </main>
      </div>

      {/* ── Bottom Nav (mobile) ── */}
      <nav
        className="bottom-nav-mobile"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(16px)',
          borderRadius: '1.5rem 1.5rem 0 0',
          boxShadow: '0 -10px 30px rgba(0,0,0,0.06)',
          paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
        }}
      >
        {BOTTOM_NAV.map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isActive(item.path) ? 'var(--primary)' : 'var(--outline)',
              fontSize: '0.625rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              padding: '0 0.75rem',
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: '1.5rem',
                fontVariationSettings: isActive(item.path) ? "'FILL' 1" : "'FILL' 0",
              }}
            >
              {item.icon}
            </span>
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  )
}
